// Team mode: two sides, and how you tell them apart.
//
// Colour is already spoken for -- every player picks their own and the trail is
// read by colour -- so the side is carried by the SHAPE of the head instead:
// Fire is a triangle, Water is a square. That survives two team-mates who
// picked neighbouring colours, and it is legible at head size where a badge or
// an outline would not be.
//
// Sides are decided by seat, not chosen: even slots are Fire, odd slots are
// Water. A 2v2 is then balanced by construction, nobody can stack a side, and
// the roster you are already looking at is the team sheet.
const TEAM_FIRE = 'fire';
const TEAM_WATER = 'water';

const TEAM_IDS = Object.freeze([TEAM_FIRE, TEAM_WATER]);

const TEAM_LABELS = Object.freeze({
  [TEAM_FIRE]: 'Team Fire',
  [TEAM_WATER]: 'Team Water'
});

const TEAM_SHORT_LABELS = Object.freeze({
  [TEAM_FIRE]: 'Fire',
  [TEAM_WATER]: 'Water'
});

// Bootstrap Icons, which the page already loads. An icon rather than an emoji
// on purpose: an emoji is a font the OS chooses, so it lands at a different
// size, weight and colour on every machine and cannot take the badge's own
// colour. These inherit both.
const TEAM_ICON_CLASSES = Object.freeze({
  [TEAM_FIRE]: 'bi-fire',
  [TEAM_WATER]: 'bi-droplet-fill'
});

// The head shape is RELATIVE TO WHO IS LOOKING: your own side keeps the normal
// round head and the opposition wears a square. It answers the question you
// actually have mid-match -- "can I cut across this one" -- which a fixed
// per-team shape does not, since you would have to remember which shape you
// are before you could read anybody else's.
//
// A spectator belongs to neither side, so they fall back to the fixed mapping
// below: without it every head would look the same to them.
const SPECTATOR_TEAM_SHAPES = Object.freeze({
  [TEAM_FIRE]: 'circle',
  [TEAM_WATER]: 'square'
});

function teamHeadShapeFor(team, viewerTeam) {
  const side = normalizeTeam(team);
  if (!side) return 'circle';
  const viewer = normalizeTeam(viewerTeam);
  if (!viewer) return SPECTATOR_TEAM_SHAPES[side];
  return side === viewer ? 'circle' : 'square';
}

// The team's own accent, for the sidebar totals and the lobby tags. Players
// keep their own colours in the arena; these only ever tint chrome.
const TEAM_ACCENTS = Object.freeze({
  [TEAM_FIRE]: '#ff7a4d',
  [TEAM_WATER]: '#4bb8ff'
});

// 2v2. A team match with three players would be a 2v1 and there is no honest
// way to seat it, so the room simply cannot start until both sides are whole.
const MIN_TEAM_PLAYERS = 4;

function isTeamId(value) {
  return value === TEAM_FIRE || value === TEAM_WATER;
}

function normalizeTeam(value) {
  return isTeamId(value) ? value : null;
}

function teamForSlotIndex(slotIndex) {
  const index = Number(slotIndex);
  if (!Number.isFinite(index) || index < 0) return TEAM_FIRE;
  return Math.floor(index) % 2 === 0 ? TEAM_FIRE : TEAM_WATER;
}

function otherTeam(team) {
  return team === TEAM_FIRE ? TEAM_WATER : TEAM_FIRE;
}

function teamLabel(team) {
  return TEAM_LABELS[team] || '';
}

function teamShortLabel(team) {
  return TEAM_SHORT_LABELS[team] || '';
}

function teamIconClass(team) {
  return TEAM_ICON_CLASSES[team] || '';
}

function teamAccent(team) {
  return TEAM_ACCENTS[team] || '';
}

// Both sides whole and equal. Odd counts are the case this exists to reject:
// seats alternate, so an odd roster is always one side short.
function isBalancedTeamRoster(playerCount) {
  const count = Math.floor(Number(playerCount) || 0);
  return count >= MIN_TEAM_PLAYERS && count % 2 === 0;
}

// Sums a per-player score array into { fire, water } using the same seat rule.
// `teams` is indexed the way the scores are, so callers that already know each
// player's side (the engine) pass it rather than re-deriving.
function sumTeamScores(scores = [], teams = []) {
  const totals = { [TEAM_FIRE]: 0, [TEAM_WATER]: 0 };
  const list = Array.isArray(scores) ? scores : [];
  for (let i = 0; i < list.length; i += 1) {
    const team = normalizeTeam(teams[i]);
    if (!team) continue;
    totals[team] += Math.max(0, Number(list[i]) || 0);
  }
  return totals;
}

export {
  TEAM_FIRE,
  TEAM_WATER,
  TEAM_IDS,
  TEAM_LABELS,
  TEAM_ACCENTS,
  MIN_TEAM_PLAYERS,
  isTeamId,
  normalizeTeam,
  teamForSlotIndex,
  otherTeam,
  teamLabel,
  teamShortLabel,
  teamHeadShapeFor,
  teamIconClass,
  teamAccent,
  isBalancedTeamRoster,
  sumTeamScores
};
